import { useContext, useMemo, useState } from 'react';

import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';

import { Autocomplete, Button, Card, Stack, styled, Typography } from '@mui/material';

import { SubmitButton } from '../../../components/buttons';
import { FormContainer } from '../../../components/forms';
import { TextField } from '../../../components/inputs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Defaults, ErrorMessages } from '../../../constants';
import { useCreateTopic } from '../../../lib/topic/hooks';
import useGetAllTopics from '../../../lib/topic/hooks/useGetAllTopics';
import { Topic } from '../../../types/Topic';

type TopicFormType = {
  title: string;
};

const TopicCreateSchema = Yup.object().shape({
  title: Yup.string().required(ErrorMessages.REQUIRED_TOPIC_TITLE),
});

function TopicsCreate() {
  const navigate = useNavigate();
  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);
  const { mutateAsync: createTopic, isLoading: isSubmitting } = useCreateTopic({
    onSuccess: () => {
      navigate('/cms/topics');
    },
  });
  const [searchTopic, setSearchTopic] = useState('');
  const debouncedSearchTopicFn = useDebouncedCallback((value: string) => {
    setSearchTopic(value);
  }, Defaults.SEARCH_DEBOUNCE_MS);
  const { data: topics, isFetching: isSearchingTopics } = useGetAllTopics({
    page: 0,
    limit: Defaults.LIST_PAGE_SIZE,
    sort: '',
    search: searchTopic,
  });

  const topicOptions = useMemo<string[]>(() => {
    const topicData = Array.isArray(topics?.data) ? (topics.data as Topic[]) : [];
    const titles = topicData
      .map((topic) => topic.title)
      .filter((title): title is string => typeof title === 'string' && title.trim().length > 0);
    return Array.from(new Set<string>(titles));
  }, [topics?.data]);

  const getTopicErrorMessage = (error: unknown) => {
    const parseMessage = (message: unknown) => {
      if (typeof message === 'string') return message;
      if (Array.isArray(message)) {
        const messages = message.filter((item): item is string => typeof item === 'string');
        if (messages.length > 0) return messages.join(', ');
      }
      return null;
    };

    if (error instanceof Error) return error.message;
    if (error && typeof error === 'object') {
      const apiError = error as {
        message?: unknown;
        error?: unknown;
        response?: { data?: { message?: unknown; error?: unknown } };
      };
      const parsedMessage = parseMessage(apiError.message) ?? parseMessage(apiError.response?.data?.message);
      if (parsedMessage) {
        if (parsedMessage.toLowerCase().includes('duplicate')) {
          return 'A similar topic already exists. Select an existing topic from autocomplete or use a different name.';
        }
        return parsedMessage;
      }
      const parsedError = parseMessage(apiError.error) ?? parseMessage(apiError.response?.data?.error);
      if (parsedError) {
        return parsedError;
      }
    }
    return ErrorMessages.SERVER_ERROR;
  };

  const handleSubmit = async (values: TopicFormType) => {
    try {
      await createTopic([
        {
          title: values.title,
          published: true,
        },
      ]);
    } catch (error) {
      setAlertSnackbar({
        message: getTopicErrorMessage(error),
        severity: 'error',
        vertical: 'top',
      });
    }
  };

  return (
    <Container>
      <TopBar>
        <Typography variant="h5" component="h1">
          Create Topic
        </Typography>
      </TopBar>

      <StyledCard>
        <Formik
          initialValues={{
            categoryId: '',
            subCategoryId: '',
            title: '',
          }}
          validationSchema={TopicCreateSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldTouched, setFieldValue }) => (
            <FormContainer>
              <Stack spacing={2}>
                <Autocomplete<string, false, false, true>
                  freeSolo
                  options={topicOptions}
                  loading={isSearchingTopics}
                  filterOptions={(options) => options}
                  inputValue={values.title}
                  onInputChange={(_, value) => {
                    setFieldValue('title', value);
                    debouncedSearchTopicFn(value);
                  }}
                  onChange={(_, value) => {
                    const nextValue = typeof value === 'string' ? value : '';
                    setFieldValue('title', nextValue);
                    debouncedSearchTopicFn(nextValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={touched.title && !!errors.title}
                      type="text"
                      name="title"
                      label="Name"
                      onBlur={() => setFieldTouched('title', true)}
                      helperText={touched.title && errors.title}
                      disabled={isSubmitting}
                    />
                  )}
                />
              </Stack>

              <ButtonContainer>
                <Buttons>
                  <SubmitButton type="submit" disabled={isSubmitting} loading={isSubmitting} variant="contained">
                    Save
                  </SubmitButton>
                  <Button onClick={() => navigate(-1)} type="button">
                    Cancel
                  </Button>
                </Buttons>
              </ButtonContainer>
            </FormContainer>
          )}
        </Formik>
      </StyledCard>
    </Container>
  );
}

export default TopicsCreate;

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const TopBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
});

const StyledCard = styled(Card)({
  display: 'flex',
});

const ButtonContainer = styled('div')({
  marginTop: '20px',
  alignSelf: 'stretch',
  flex: 1,
  display: 'flex',
  justifyContent: 'space-between',
});

const Buttons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',

  '& > *': {
    margin: '5px',
  },
});
